<template>
  <div v-if="deck" class="card-counter">
    <h1>Card counter <span class="small text-muted">(experimental)</span></h1>
    <h2>{{ deck.name }}</h2>
    <a
      href="#"
      v-b-modal.card-counter-help-modal
      @click.prevent
      class="info-tooltip"
    >
      How do I use the card counter?
    </a>

    <b-modal id="card-counter-help-modal" ok-only title="Card counter help">
      <p>
        You can use the card counter to keep track of the state of your own deck
        or your opponents (or open two browser windows or tabs and keep track of
        both!)
      </p>
      <p>
        When keeping track of <strong>your own deck</strong>, use the buttons in
        each row to move cards between your
        <span class="text-primary text-nowrap"
          >draw pile <font-awesome-icon :icon="['far', 'layer-group']" /> </span
        >, your
        <span class="text-danger text-nowrap"
          >discard pile <font-awesome-icon :icon="['far', 'trash']" /> </span
        >, and your
        <span class="text-success text-nowrap"
          >hand <font-awesome-icon :icon="['far', 'hand-paper']" /> </span
        >. Look at the <em>Draw probability</em> column in the table to see the
        odds of drawing a specific card next, or use the
        <strong>Probability calculator</strong> at the top of the page to see
        the odds of drawing a card meeting your chosen criteria.
      </p>
      <p>
        When keeping track of <strong>your opponent's</strong> deck, you usually
        won't know what's in their hand (although card effects may sometimes
        change) that. Instead, you can set the <em>hand size</em> in the
        <strong>Probability calculator</strong> to their current hand size.
        Then, look at the <em>In hand size n</em> column in the table for the
        odds they are currently holding at least one copy of that specific card,
        or use the <strong>Probability calculator</strong>
        to see the odds they're holding a card meeting your chosen criteria.
      </p>
    </b-modal>
    <div class="my-4">
      <h3>Probability calculator</h3>
      <b-form inline>
        The probability of drawing
        <b-form-select
          :options="cardTypes"
          v-model="selectedType"
          class="mx-1 mb-1"
        />

        with value
        <b-form-select
          :options="comparatorTypes"
          v-model="valueComparator"
          class="mx-1 mb-1"
        />
        <b-form-input
          v-if="valueComparator"
          type="number"
          number
          v-model="cardValue"
          class="mx-1 mb-1"
        />
        and boost
        <b-form-select
          :options="comparatorTypes"
          v-model="boostComparator"
          class="mx-1 mb-1"
        />
        <b-form-input
          v-if="boostComparator"
          type="number"
          number
          v-model="boostValue"
          class="mx-1 mb-1"
        />
        is
        {{
          `${(filteredCount / inDraw.quantity).toLocaleString("en", {
            style: "percent",
            maximumFractionDigits: 1,
          })} (${filteredCount}/${inDraw.quantity})`
        }}
      </b-form>
      <b-form inline>
        In a hand size of
        <b-form-input
          type="number"
          number
          v-model="handSize"
          class="mx-1 mb-1"
          min="0"
        />
        the probability of having at least one matching card is
        {{
          handSizeProbability.toLocaleString("en", {
            style: "percent",
            maximumFractionDigits: 4,
          })
        }}
      </b-form>
    </div>
    <div>
      <h4>Simulate</h4>
      <div>
        Randomly draw and discard to simulate various game states for testing
        and analysis.
      </div>
      <b-button
        @click="drawRandom('hand')"
        variant="outline-success"
        size="sm"
        class="mb-3 mr-1"
        ><font-awesome-icon :icon="['far', 'hand-paper']" /> Draw random
        card</b-button
      >
      <b-button
        @click="drawRandom('discard')"
        variant="outline-danger"
        size="sm"
        class="mb-3 mr-1"
        ><font-awesome-icon :icon="['far', 'trash']" /> Discard random
        card</b-button
      >
    </div>
    <div v-if="deck.cards.length">
      <h3>Deck state tables</h3>
      <b-button
        @click="resetDeck"
        variant="outline-warning"
        size="sm"
        class="mb-3 mr-1"
        ><font-awesome-icon :icon="['far', 'exclamation-triangle']" /> Reset
        deck</b-button
      >
      <b-tabs
        nav-wrapper-class="sticky-top bg-body overflow-auto"
        nav-class="flex-nowrap text-nowrap"
      >
        <b-tab
          title="Draw pile"
          active
          :disabled="inDraw.quantity === 0"
          :title-link-class="[inDraw.quantity ? 'text-primary' : null]"
        >
          <template #title>
            <font-awesome-icon :icon="['far', 'layer-group']" />

            Draw
            <b-badge :variant="inDraw.quantity ? 'primary' : null">{{
              inDraw.quantity
            }}</b-badge>
          </template>
          <b-form-checkbox v-model="compact" switch class="m-2">
            Compact view
          </b-form-checkbox>
          <b-form-group>
            <b-form-input
              v-model="filter"
              placeholder="Filter"
              type="search"
              trim
            ></b-form-input>
          </b-form-group>
          <CardCounterTable
            :items="inDraw"
            :handSize="handSize"
            @umcc:discard="moveCard($event, 'draw', 'discard')"
            @umcc:hand="moveCard($event, 'draw', 'hand')"
            showDiscard
            showHand
            showProbability
            :compact="compact"
            :sortBy.sync="sortBy"
            :sortDesc.sync="sortDesc"
            :filter="filter"
          />
        </b-tab>

        <b-tab
          title="Discard pile"
          :disabled="inDiscard.quantity === 0"
          :title-link-class="[inDiscard.quantity ? 'text-danger' : null]"
        >
          <template #title>
            <font-awesome-icon :icon="['far', 'trash']" />

            Discard
            <b-badge :variant="inDiscard.quantity ? 'danger' : null">{{
              inDiscard.quantity
            }}</b-badge>
          </template>
          <b-form-checkbox
            v-model="compact"
            name="check-button"
            switch
            class="m-2"
          >
            Compact view
          </b-form-checkbox>
          <b-form-group>
            <b-form-input
              v-model="filter"
              placeholder="Filter"
              type="search"
              trim
            ></b-form-input>
          </b-form-group>
          <CardCounterTable
            :items="inDiscard"
            @umcc:draw="moveCard($event, 'discard', 'draw')"
            @umcc:hand="moveCard($event, 'discard', 'hand')"
            showDraw
            showHand
            :compact="compact"
            :sortBy.sync="sortBy"
            :sortDesc.sync="sortDesc"
            :filter="filter"
          />
        </b-tab>

        <b-tab
          title="Hand"
          :disabled="inHand.quantity === 0"
          :title-link-class="[inHand.quantity ? 'text-success' : null]"
        >
          <template #title>
            <font-awesome-icon :icon="['far', 'hand-paper']" />
            Hand
            <b-badge :variant="inHand.quantity ? 'success' : null">{{
              inHand.quantity
            }}</b-badge>
          </template>
          <b-form-checkbox
            v-model="compact"
            name="check-button"
            switch
            class="m-2"
          >
            Compact view
          </b-form-checkbox>
          <b-form-group>
            <b-form-input
              v-model="filter"
              placeholder="Filter"
              type="search"
              trim
            ></b-form-input>
          </b-form-group>
          <CardCounterTable
            :items="inHand"
            @umcc:draw="moveCard($event, 'hand', 'draw')"
            @umcc:discard="moveCard($event, 'hand', 'discard')"
            showDraw
            showDiscard
            :compact="compact"
            :sortBy.sync="sortBy"
            :sortDesc.sync="sortDesc"
            :filter="filter"
          /> </b-tab
      ></b-tabs>
    </div>
  </div>
</template>

<script>
// import DeckAnalysis from "@/components/DeckAnalysis.vue";
import CardCounterTable from "@/components/CardCounterTable.vue";
import api from "@/api";
import { UnmatchedDeck } from "@/Unmatched";

export default {
  name: "CardCounter",
  metaInfo() {
    const title = this.deck ? `${this.deck.name} | Card counter` : undefined;
    return {
      title,
    };
  },
  components: { CardCounterTable },
  data() {
    return {
      deck: null,
      cardTypes: [
        { value: ["attack"], text: "attack" },
        { value: ["attack", "versatile"], text: "attack or versatile" },
        { value: ["versatile"], text: "versatile" },
        {
          value: ["defence", "defense", "versatile"],
          text: "defence or versatile",
        },
        { value: ["defence", "defense"], text: "defence" },
        { value: ["scheme"], text: "scheme" },
        { value: null, text: "any card type" },
      ],
      selectedType: null,
      comparatorTypes: [
        { value: null, text: "any" },
        { value: (x, y) => x > y, text: "greater than" },
        { value: (x, y) => x >= y, text: "greater than or equal" },
        { value: (x, y) => x === y, text: "equal" },
        { value: (x, y) => x <= y, text: "less than or equal" },
        { value: (x, y) => x < y, text: "less than" },
      ],
      valueComparator: null,
      cardValue: 0,
      boostComparator: null,
      boostValue: 0,
      handSize: 5,
      filter: "",
      sortBy: "title",
      sortDesc: false,
    };
  },
  props: {
    slug: {
      type: String,
      required: true,
    },
  },
  computed: {
    compact: {
      get() {
        if ("cardCounterCompact" in this.$store.state.userPreferences) {
          return this.$store.state.userPreferences.cardCounterCompact;
        }
        return false;
      },
      set(value) {
        this.$store.commit("setPreference", {
          key: "cardCounterCompact",
          value: value,
        });
      },
    },
    filteredCount() {
      const matchingCards = this.inDraw.cards.filter((card) => {
        var matchesType = this.selectedType
          ? this.selectedType.includes(card.type)
          : true;
        var matchesValue = this.valueComparator
          ? this.valueComparator(card.value, this.cardValue) &&
            card.type !== "scheme"
          : true;
        var matchesBoost = this.boostComparator
          ? this.boostComparator(card.boost, this.boostValue)
          : true;
        return matchesType && matchesValue && matchesBoost;
      });
      return this.totalCards(matchingCards);
    },
    handSizeProbability() {
      var prob = 1;
      if (this.handSize < 1 || this.filteredCount === 0) return 0;
      if (this.handSize > this.inDraw.quantity - this.filteredCount) return 1;
      for (var i = 0; i < this.handSize; ++i) {
        prob *= 1 - this.filteredCount / (this.inDraw.quantity - i);
      }
      return 1 - prob;
    },
    inDraw() {
      const cards = this.deck.cards
        .map((card) => ({
          ...card,
          quantity: card.quantity.draw,
          _showDetails: !this.compact,
        }))
        .filter((card) => card.quantity > 0);

      return { cards: cards, quantity: this.totalCards(cards) };
    },

    inDiscard() {
      const cards = this.deck.cards
        .map((card) => ({
          ...card,
          quantity: card.quantity.discard,
          _showDetails: !this.compact,
        }))
        .filter((card) => card.quantity > 0);

      return { cards: cards, quantity: this.totalCards(cards) };
    },
    inHand() {
      const cards = this.deck.cards
        .map((card) => ({
          ...card,
          quantity: card.quantity.hand,
          _showDetails: !this.compact,
        }))
        .filter((card) => card.quantity > 0);

      return { cards: cards, quantity: this.totalCards(cards) };
    },
  },
  methods: {
    resetDeck() {
      this.deck.cards.forEach(
        (card) =>
          (card.quantity = {
            deck: card.quantity.deck,
            draw: card.quantity.deck,
            hand: 0,
            discard: 0,
          })
      );
    },
    totalCards(cardArray) {
      return cardArray.reduce((accumulator, card) => {
        return accumulator + card.quantity;
      }, 0);
    },
    moveCard(slug, from, to) {
      const card = this.deck.cards.find((card) => card.slug === slug);
      card.quantity[from]--;
      card.quantity[to]++;
    },
    drawRandom(to) {
      // Need to weight the random draw by the number of copies in the draw pile

      if (this.inDraw.quantity) {
        const randomChoice = Math.floor(Math.random() * this.inDraw.quantity);
        var chosenCard;
        var currentIndex = 0;

        this.inDraw.cards.some((card) => {
          chosenCard = card;
          currentIndex += card.quantity;
          return randomChoice < currentIndex;
        });

        this.moveCard(chosenCard.slug, "draw", to);
      }
    },
    getDeck(slug) {
      api
        .getDBDeck(slug)
        .then((data) => {
          this.deck = data;
          this.deck.cards = data.cards.map((card) => ({
            ...card,
            quantity: {
              deck: card.quantity,
              draw: card.quantity,
              hand: 0,
              discard: 0,
            },
          }));
        })
        .catch(() => {
          // If a UmDb deck doesn't exist, try getting a user deck
          api
            .getDeck(slug)
            .then((deck) => {
              this.deck = new UnmatchedDeck(deck.deck_data);
              this.deck.name = deck.name;
              this.deck.cards = this.deck.cards.map((card) => ({
                ...card.toJSON(),
                slug: card._uuid,
                quantity: {
                  deck: card.quantity,
                  draw: card.quantity,
                  hand: 0,
                  discard: 0,
                },
              }));
            })
            .catch(() => {
              this.$router.replace("/error");
            });
        });
    },
  },
  mounted() {
    this.getDeck(this.slug);
  },
  beforeRouteUpdate(to, from, next) {
    this.getDeck(to.params.slug);
    next();
  },
};
</script>
